import React from "react";

interface Issue {
  id: string;
  type: "contrast" | "aria" | "keyboard" | "labels" | "images";
  message: string;
  node?: string;
  severity: "low" | "medium" | "high";
}

function checkContrast(): Issue[] {
  const issues: Issue[] = [];
  const nodes = document.querySelectorAll<HTMLElement>("*" );
  nodes.forEach((el) => {
    const style = getComputedStyle(el);
    const fg = style.color;
    const bg = style.backgroundColor;
    // naive skip if transparent background
    if (!bg || bg === "rgba(0, 0, 0, 0)") return;
    // defer actual ratio calculation; placeholder
    // In production, integrate with axe-core or color-contrast-checker
    // Here we flag if both colors are grayscale and close
    function toGray(c: string) {
      const m = c.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (!m) return 0;
      const r = parseInt(m[1], 10);
      const g = parseInt(m[2], 10);
      const b = parseInt(m[3], 10);
      return Math.round(0.299 * r + 0.587 * g + 0.114 * b);
    }
    const fgG = toGray(fg);
    const bgG = toGray(bg);
    if (Math.abs(fgG - bgG) < 40) {
      issues.push({
        id: Math.random().toString(36).slice(2),
        type: "contrast",
        message: "Potential low contrast detected",
        node: el.tagName.toLowerCase(),
        severity: "medium",
      });
    }
  });
  return issues.slice(0, 50);
}

function checkImages(): Issue[] {
  const issues: Issue[] = [];
  document.querySelectorAll<HTMLImageElement>("img").forEach((img) => {
    if (!img.alt || img.alt.trim() === "") {
      issues.push({
        id: Math.random().toString(36).slice(2),
        type: "images",
        message: "Image missing alt text",
        node: img.src.split("/").pop(),
        severity: "low",
      });
    }
  });
  return issues;
}

function checkLabels(): Issue[] {
  const issues: Issue[] = [];
  document.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    "input, select, textarea",
  ).forEach((el) => {
    const id = el.getAttribute("id");
    if (!id) return;
    const label = document.querySelector(`label[for='${id}']`);
    if (!label) {
      issues.push({
        id: Math.random().toString(36).slice(2),
        type: "labels",
        message: "Form control missing associated label",
        node: id,
        severity: "medium",
      });
    }
  });
  return issues;
}

export const AccessibilityChecker: React.FC = () => {
  const [issues, setIssues] = React.useState<Issue[]>([]);
  const [running, setRunning] = React.useState(false);

  const runChecks = () => {
    setRunning(true);
    setTimeout(() => {
      const results = [
        ...checkContrast(),
        ...checkImages(),
        ...checkLabels(),
      ];
      setIssues(results);
      setRunning(false);
    }, 10);
  };

  return (
    <div className="mt-6 border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold">Accessibility Checker</h3>
        <button
          onClick={runChecks}
          className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
          disabled={running}
        >
          {running ? "Running..." : "Run Checks"}
        </button>
      </div>
      {issues.length === 0 ? (
        <p className="text-gray-600 text-sm">No issues found yet. Click Run Checks.</p>
      ) : (
        <ul className="space-y-2">
          {issues.map((i) => (
            <li key={i.id} className="flex items-start gap-3 p-2 rounded border">
              <span
                className={`inline-block px-2 py-0.5 rounded text-xs ${
                  i.severity === "high"
                    ? "bg-red-100 text-red-700"
                    : i.severity === "medium"
                    ? "bg-yellow-100 text-yellow-700"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {i.severity}
              </span>
              <div>
                <div className="text-sm font-medium capitalize">{i.type}</div>
                <div className="text-sm text-gray-700">{i.message}</div>
                {i.node && <div className="text-xs text-gray-500">Node: {i.node}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AccessibilityChecker;
